import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { SplitHorizontalIcon } from '@phosphor-icons/react';
import type { EntityAssociationItem } from '@erp/rpc';
import { confirmedSplitParts } from './confirmedSplitMatch';
import { SplitMatchCell } from './SplitMatchCell';
import { useReviewSplitProposal } from './splitProposalReviewContext';
import {
  splitSuggestionOf,
  type SplitProposalLineInput,
  type SplitSuggestionSummary,
} from './splitProposalCardData';

interface SplitSuggestionMarkerProps extends SplitSuggestionSummary {
  /** Opens the Related lines tab, where the suggestion is explained. */
  onReview?: () => void;
}

/**
 * The match cell of an invoice line that has a split suggestion. Like the
 * aggregation marker it replaces the per-charge chips, which would imply a
 * single match; the details and the Accept and Dismiss buttons live in the
 * Related lines tab. The text stays short because the match column is narrow;
 * the tooltip carries the detail.
 */
export function SplitSuggestionMarker({
  status,
  partCount,
  onReview,
}: SplitSuggestionMarkerProps) {
  const gt = useGT();
  const title =
    status === 'proposed'
      ? gt(
          'Suggested split into {count} parts. Open Related lines to review.',
          {
            count: String(partCount),
            context:
              'tooltip on the marker of an AP invoice line with a suggested split',
          },
        )
      : gt(
          'Several combinations add up to this line. Open Related lines to review.',
          {
            context:
              'tooltip on the marker of an AP invoice line that could be split in several ways',
          },
        );
  return (
    <button
      type="button"
      title={title}
      onClick={onReview}
      className="flex items-center gap-1.5 whitespace-nowrap text-left text-xs text-sky-700 hover:underline dark:text-sky-300"
    >
      <SplitHorizontalIcon className="h-3.5 w-3.5 shrink-0" />
      <span>
        {status === 'proposed' ? (
          <T context="marker on an AP invoice line that has a suggested split; opens the Related lines tab">
            Suggested split
          </T>
        ) : (
          <T context="marker on an AP invoice line that could be split in several ways; opens the Related lines tab">
            Several ways to split
          </T>
        )}
      </span>
    </button>
  );
}

/**
 * The match cell of a Line Items row: the marker when the line has a split
 * suggestion, every part when AP accepted one, otherwise the cell it normally
 * shows. The marker opens the Related lines tab through
 * `SplitProposalReviewContext`.
 */
export function MatchCellOrSplitSuggestion({
  line,
  associations,
  money,
  children,
}: {
  line: SplitProposalLineInput;
  associations: readonly EntityAssociationItem[] | undefined;
  money: (amount: string) => string;
  children: ReactNode;
}) {
  const onReview = useReviewSplitProposal();
  const suggestion = splitSuggestionOf(line);
  if (suggestion)
    return <SplitSuggestionMarker {...suggestion} onReview={onReview} />;
  const parts = confirmedSplitParts(associations);
  if (parts.length > 0) return <SplitMatchCell parts={parts} money={money} />;
  return <>{children}</>;
}
